import {Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {CustomerInteractionService} from "../../../service/customer-interaction.service";
import {CustomerInteraction} from "../../../model/customerInteraction";
import {Property} from "../../../model/property";
import {UrlService} from "../../../service/url.service";
import {DropzoneComponent} from "../../dropzone/dropzone.component";
import {PropertyService} from "../../../service/property.service";
import {ProductService} from "../../../service/product.service";
import {PropertyFormComponent} from "../../new-order/property-form/property-form.component";
import {CustomerInteractionResponse} from "../../../model/CustomerInteractionResponse";
import {InteractionStatus} from "../../../model/interactionStatus";
import {NgxIndexedDBService} from "ngx-indexed-db";
import {Order} from "../../../model/order";
import {HttpHeaders} from "@angular/common/http";
import {faArrowLeft} from "@fortawesome/free-solid-svg-icons";
import * as Sentry from "@sentry/angular";

/**
 * Payload emitted after a new customer interaction was created by the user
 */
export interface NewCustomerInteractionResult {
  customerInteraction: CustomerInteraction;
  // Number of attached files that were uploaded successfully to the new customer interaction
  uploadedFileCount: number;
  // Names of attached files that could not be uploaded to the new customer interaction
  failedFileNames: string[];
}

@Component({
  selector: 'app-customer-interaction',
  templateUrl: './customer-interaction.component.html',
  styleUrls: ['./customer-interaction.component.css']
})
export class CustomerInteractionComponent implements OnInit {

  @ViewChild('authorityDropzone') authorityDropzoneComponent: DropzoneComponent;
  @ViewChild('textResponse') textArea: ElementRef;
  @ViewChild(PropertyFormComponent) propertyFormComponent: PropertyFormComponent;

  @Output() closeCiModalAndUpdateProperty = new EventEmitter<NewCustomerInteractionResult>();

  @Input() customerInteractionId: string;
  @Input() property: Property;
  @Input() insideCard: boolean = false;

  customerInteraction: CustomerInteraction;
  previousUrl: string = "";
  inspectingProperty: Property;
  platformProfile;
  showInspectChangedDataModal: boolean = false;
  showAddFileModal: boolean = false;

  isLoading: boolean = false;
  doneLoading: boolean = false;
  errorDuringResponse = false;
  isInvalid: boolean = false;
  isMissingDocumentSelection: boolean = false;
  // A new customer interaction always needs a description, attached files are optional
  isMissingDescription: boolean = false;
  // Set if a new customer interaction could not be created
  errorDuringCreation: boolean = false;

  // Scope of a new customer interaction: either a general request or one referring to specific documents
  interactionScope: 'GENERAL' | 'DOCUMENTS' = 'GENERAL';
  selectedDocumentIds: string[] = [];
  products = [];

  protected readonly faArrowLeft = faArrowLeft;

  constructor(private route: ActivatedRoute, private customerInteractionService: CustomerInteractionService, private urlService: UrlService, private router: Router,
              private propertyService: PropertyService, private dbService: NgxIndexedDBService,
              private productService: ProductService) {
  }

  ngOnInit(): void {

    this.platformProfile = JSON.parse(sessionStorage.getItem("platform"))

    this.productService.getProducts().subscribe(products => {
      this.products = products
    });

    this.urlService.previousUrl$.subscribe((previousUrl: string) => {
      this.previousUrl = previousUrl
    })

    // If the customer interaction id is not yet set try to get it from the url
    if(!this.customerInteractionId){
        this.customerInteractionId = this.route.snapshot.params.customerInteractionId;
    }

    // If the property is not yet set via the Input try to get it from the history state
    if(!this.property){
        this.property = history.state[0];
    }

    if (this.customerInteractionId === 'new' && !this.property) {
      this.back();
    }

    if (this.customerInteractionId !== 'new') {
      this.customerInteractionService.getCustomerInteractionById(this.customerInteractionId).subscribe(ciResult => {
        this.customerInteraction = JSON.parse(JSON.stringify(ciResult));
        this.customerInteraction.responses.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        if (!this.property) {
          this.propertyService.getPropertyById(this.customerInteraction.propertyId).subscribe(propertyResult => {
              this.property = propertyResult;
          });
        }
      });
    }
  }

  back() {
    if (this.previousUrl === undefined || this.previousUrl.includes("/login")) {
      this.router.navigate(['/home']);
    } else {
      this.router.navigate([this.previousUrl]);
    }
  }

  updateCurrentCustomerInteraction() {
    this.customerInteractionService.getCustomerInteractionById(this.customerInteractionId, {headers: new HttpHeaders({'showing-spinner': ''})}).subscribe(
        res => {
          this.customerInteraction = JSON.parse(JSON.stringify(res));
          this.customerInteraction.responses.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
          this.isLoading = false;
          this.errorDuringResponse = false;
          this.doneLoading = true;
    });
  }

  /**
   * Finishes the customer interaction process by submitting a text response and/or files.
   * For a new customer interaction the text is used as description and the files are attached to the created customer interaction.
   */
  public async finishCustomerInteraction(): Promise<void>{
    // Get the user input
    const textResponse = this.textArea.nativeElement.value;
    const tempFiles = this.getSelectedFiles();
    const hasText = !!textResponse && textResponse.trim() !== '';

    if (this.customerInteractionId === 'new') {
      // A new request always needs a description, the attached files are optional
      if (!hasText) {
        this.isMissingDescription = true;
        return;
      }

      // If the request refers to specific documents at least one of them has to be selected
      if (this.interactionScope === 'DOCUMENTS' && this.selectedDocumentIds.length === 0) {
        this.isMissingDocumentSelection = true;
        return;
      }
    }
    // If no input is given show an error by setting the isInvalid flag to true
    else if (!hasText && tempFiles.length === 0) {
      this.isInvalid = true;
      return;
    }

    this.isLoading = true;
    this.doneLoading = false;
    this.isInvalid = false;
    this.isMissingDocumentSelection = false;
    this.isMissingDescription = false;
    this.errorDuringCreation = false;

    if (this.customerInteractionId === 'new') {
      await this.createNewCustomerInteraction(textResponse, tempFiles);
      return;
    }

    try{
      await this.addResponseToCustomerInteraction(textResponse, tempFiles)
      this.updateOrdersInSessionStorage();
      this.updateCurrentCustomerInteraction();
      this.resetInputs();
    }
    catch (error){
      Sentry.captureException(error);
      this.errorDuringResponse = true;
      this.isLoading = false
    }
  }

  /**
   * If a customer interaction is being updated the sessionStorage has to be updated too because otherwise outdated information are being presented to the user the user
   * until all the orders are being updated again.
   */
  updateOrdersInSessionStorage() {
    this.dbService.getByKey('orders', this.property.orderid).subscribe((order: Order) => {
      if (order) {
        order.properties.forEach(p => {
          p.customerInteractions.forEach(cI => {
            if (cI.id === this.customerInteractionId) {
              cI.status = InteractionStatus.WAITING_FOR_DOCESTATE;
              this.dbService.update('orders', order).subscribe();
            }
          })
        })
      }
    })
  }

  openInspectChangedDataModal() {
    this.showInspectChangedDataModal = true;
  }

  /**
   * Decodes a Base64-encoded string into its original Unicode (UTF-8) representation,
   * with fallback for legacy-encoded data.
   *
   * This function is designed to handle both:
   *   - Proper UTF-8-encoded Base64 strings (the modern, recommended way)
   *   - Legacy Base64 strings created via JavaScript's btoa() on non-ASCII data,
   *     which are actually encoded as Latin-1 (ISO-8859-1) and may be corrupted
   *     when interpreted as UTF-8.
   *
   * Decoding process:
   *   1. Attempts to decode as UTF-8. If the result contains Unicode replacement
   *      characters ("�"), it likely indicates a legacy encoding issue.
   *   2. In such cases, decodes the Base64 as ISO-8859-1 (Latin-1) instead,
   *      which restores Western European special characters (ä, ö, ü, ß, etc.).
   *
   * Note: Characters outside the Latin-1 range (such as emojis or non-Western scripts)
   *       may not be recoverable if the data was encoded incorrectly.
   *
   * @param base64 - The Base64-encoded string to decode.
   * @returns The decoded, human-readable Unicode string.
   */
  public decodeBase64(base64: string): string {
    const utf8 = new TextDecoder('utf-8').decode(Uint8Array.from(atob(base64), c => c.charCodeAt(0)));
    if (utf8.includes('�')) {
      // Fallback to Latin-1 if replacement chars are found
      return new TextDecoder('iso-8859-1').decode(Uint8Array.from(atob(base64), c => c.charCodeAt(0)));
    }
    return utf8;
  }

  /**
   * Get the response message that should be displayed inside the "chat"
   * @param response customer interaction response
   */
  getTextFromResponse(response: CustomerInteractionResponse) {
    switch (response.type) {
      case "application/property-json":
        return "Objektdaten angepasst.";
      case "application/pdf":
        return "Datei hochgeladen.";
      case "text/plain":
        return this.decodeBase64(response.message);
    }
  }

  isResponseClickable(response: CustomerInteractionResponse): boolean {
    return response.type === "application/property-json" || response.type === "application/pdf";
  }

  base64ToBlob(base64) {
    const byteCharacters = atob(base64);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);

      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, {type: "application/pdf"});
  }

  /**
   * Display the authority file associated with a customer interaction response
   *
   * @param customerInteractionResponseId Id of the customer interaction response for which the authority should be displayed
   */
  private showAuthority(customerInteractionResponseId: string): void {
    this.propertyService.getCustomerInteractionResponsePdf(this.property.id, customerInteractionResponseId).subscribe((base64String: string) => {
      const blob = this.base64ToBlob(base64String);
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl);
    })
  }

  inspectChangedData(response) {
    switch (response.type) {
      case "application/property-json":
        this.inspectingProperty = JSON.parse(this.decodeBase64(response.message));
        this.openInspectChangedDataModal();
        break;
      case "application/pdf":
        this.showAuthority(response.id);
        break;
    }
  }

  getLatestResponseDate() {
    if (this.customerInteraction) {
      if (this.customerInteraction.responses !== null && this.customerInteraction.responses.length > 0)
        return this.customerInteraction.responses[this.customerInteraction.responses.length - 1].date;
      else
        return this.customerInteraction.date
    } else {
      return new Date();
    }

  }

  /**
   * Refresh the customerInteractionPage when a Problem occured during the file upload
   */
  reloadPage(): void {
    location.reload();
  }

  /**
   * Creates a new customer interaction as a user and attaches the given files to it afterwards.
   *
   * If the customer interaction itself could not be created an error is shown and all inputs are kept, so the user can try again.
   * Once the customer interaction exists it is always emitted (which closes the modal), even if single file uploads failed,
   * to prevent the user from creating the same request twice. The names of the failed files are emitted as well so the parent can inform the user.
   *
   * @param textResponse Text that is used as description of the customer interaction
   * @param files Files that should be attached to the new customer interaction
   */
  private async createNewCustomerInteraction(textResponse: string, files: File[]): Promise<void> {
    let addedCi: CustomerInteraction;
    try {
      addedCi = await this.addNewCustomerInteraction(textResponse);
    } catch (error) {
      Sentry.captureException(error);
      this.errorDuringCreation = true;
      this.isLoading = false;
      return;
    }

    const failedFileNames = await this.uploadFilesToCustomerInteraction(addedCi.id, this.property.id, files);

    this.isLoading = false;
    this.doneLoading = true;
    this.closeCiModalAndUpdateProperty.emit({
      customerInteraction: addedCi,
      uploadedFileCount: files.length - failedFileNames.length,
      failedFileNames: failedFileNames
    });
  }

  /**
   * Adds a customer interaction as a user containing text.
   *
   * @param textResponse Text that the customer interaction should contain
   * @return The created customer interaction
   */
  private async addNewCustomerInteraction(textResponse: string): Promise<CustomerInteraction> {
    const associatedIds = this.interactionScope === 'DOCUMENTS' ? this.selectedDocumentIds : [];
    const addedCi = await this.customerInteractionService.addUserCustomerInteraction(this.property.id, textResponse, associatedIds).toPromise();
    this.customerInteractionId = addedCi.id;
    return addedCi;
  }

  /**
   * Uploads the given files one by one as file responses (application/pdf) of a customer interaction.
   * Each file becomes its own response, analogous to the file attachments of a customer interaction created in the AdminUI.
   * A failing upload does not stop the upload of the remaining files.
   *
   * @param customerInteractionId Id of the customer interaction the files should be attached to
   * @param propertyId Id of the property the customer interaction belongs to
   * @param files Files that should be uploaded
   * @return Names of all files that could not be uploaded
   */
  private async uploadFilesToCustomerInteraction(customerInteractionId: string, propertyId: string, files: File[]): Promise<string[]> {
    const failedFileNames: string[] = [];
    for (const file of files) {
      try {
        await this.customerInteractionService.uploadAuthorities([file], customerInteractionId, propertyId).toPromise();
      } catch (error) {
        Sentry.captureException(error);
        failedFileNames.push(file.name);
      }
    }
    return failedFileNames;
  }

  /**
   * Adds a response to the current customer interaction.
   * If the input contains text and the drop zone contains at least one file two responses are added to the customer interaction
   *
   * @param textResponse Text the user wants to add to the customer interaction response
   * @param files Files the user wants to add to the customer interaction response
   */
  private async addResponseToCustomerInteraction(textResponse: string, files: any[]): Promise<void> {
    // If the user entered text add a text response
    if (textResponse && textResponse.trim() !== '') {
      // Encodes a Unicode (UTF-8) string safely into a Base64-encoded string.
      const base64TextResponse = btoa(String.fromCharCode(...new TextEncoder().encode(textResponse)));

      await this.customerInteractionService.uploadFreeTextResponse(this.customerInteraction.propertyId, this.customerInteractionId, base64TextResponse).toPromise();
    }
    // If the user added a file add a file response
    if (files.length > 0) {
      for(let file of files){
        await this.customerInteractionService.uploadAuthorities([file], this.customerInteractionId, this.customerInteraction.propertyId).toPromise();
      }
    }
  }

  /**
   * Returns all files the user added to the dropzone
   */
  private getSelectedFiles(): File[] {
    return (this.authorityDropzoneComponent?.files ?? []).map(file => file.file);
  }

  /**
   * Resolves the product name belonging to a documents short name
   *
   * @param shortName Short name of the document
   * @return The long product name or the short name if no matching product exists
   */
  public getLongName(shortName: string): string {
    const product = this.products.find(p => p.nameShort === shortName);
    return product ? product.name_long : shortName;
  }

  /**
   * Adds or removes a document from the selection a new customer interaction refers to
   *
   * @param documentId Id of the document that was clicked
   */
  public toggleDocumentSelection(documentId: string): void {
    const index = this.selectedDocumentIds.indexOf(documentId);
    if (index === -1) {
      this.selectedDocumentIds.push(documentId);
    } else {
      this.selectedDocumentIds.splice(index, 1);
    }
    this.isMissingDocumentSelection = false;
  }

  /**
   * Resets all inputs (text input and file input) used for answering a custom interaction
   */
  private resetInputs(): void{
    this.authorityDropzoneComponent.files = [];
    this.authorityDropzoneComponent.fileInput.nativeElement.value = null;
    this.textArea.nativeElement.value = "";
  }


  /**
   * takes the index of a file in the array and deletes it
   * @param index of a file
   */
  public deleteFile(index: number): void {
    this.authorityDropzoneComponent.files.splice(index, 1);
    this.authorityDropzoneComponent.fileInput.nativeElement.value = null;

  }

}
